// File: src/components/pages/tracker/PublishTracker.jsx
// Split-panel view:
//   Left  — existing TrackerLevelsTemplate config expanded to Level / Heading / Item / Type
//   Right — placeholder for future TrackerLevels comparison (later phase)

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { LayoutTemplate, AlertCircle, Clock } from 'lucide-react';
import { trackerTemplateAPI, trackerLevelsTemplateAPI } from '../../../services/api';
import '../../../styles/unified.css';

const TYPE_COLOURS = {
  'Checkbox':  '#667eea',
  'Rating':    '#10b981',
  'Pass/Fail': '#f59e0b',
  'Text':      '#6b7280',
};

const PublishTracker = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [isLoading,  setIsLoading]  = useState(true);
  const [loadError,  setLoadError]  = useState(null);
  const [expandedLevels, setExpandedLevels] = useState([]);
  const [configDate,     setConfigDate]     = useState(null);

  // ── Load and expand configuration ───────────────────────────────────────────
  // 1. Fetch TrackerTemplates (for full item/type data)
  // 2. Fetch TrackerLevelsTemplate (for the saved levels/template mapping)
  // 3. Join them to produce the full expanded structure
  const loadAndExpand = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [templatesRes, configsRes] = await Promise.all([
        trackerTemplateAPI.getTemplates(getAccessTokenSilently),
        trackerLevelsTemplateAPI.getConfigs(getAccessTokenSilently)
      ]);

      if (!templatesRes.success) {
        setLoadError('Failed to load templates.');
        return;
      }

      if (!configsRes.success || !configsRes.data || configsRes.data.length === 0) {
        setExpandedLevels([]);
        return;
      }

      const templateMap = {};
      (templatesRes.data || []).forEach(t => { templateMap[t.templateId] = t; });

      const latest = configsRes.data[0]; // newest-first
      setConfigDate(latest.createdAt);

      const expanded = (latest.levels || []).map(lv => ({
        levelNumber: lv.level,
        templates: (lv.templates || []).map(selTpl => {
          const full = templateMap[selTpl.templateId];
          return {
            templateId: selTpl.templateId,
            heading:    selTpl.heading,
            items:      full?.items || []
          };
        })
      }));

      setExpandedLevels(expanded);
    } catch (err) {
      console.error('Error loading publish data:', err);
      setLoadError(err.message || 'Failed to load configuration.');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { loadAndExpand(); }, [loadAndExpand]);

  const formatDate = (d) => !d ? '' : new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="publish-split-layout">

      {/* ── LEFT PANEL — Current Configuration ── */}
      <div className="publish-panel publish-panel-left">
        <div className="publish-panel-header">
          <h3 className="publish-panel-title">Current Configuration</h3>
          {configDate && (
            <span className="publish-panel-meta">
              <Clock size={13} />
              Last saved {formatDate(configDate)}
            </span>
          )}
        </div>

        {loadError && (
          <div className="error-banner" style={{ margin: '1rem 0' }}>
            <AlertCircle size={16} /><span>{loadError}</span>
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading configuration…</p>
          </div>
        ) : expandedLevels.length === 0 ? (
          <div className="empty-state">
            <LayoutTemplate size={40} style={{ color: '#94a3b8' }} />
            <p>No configuration found.</p>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8' }}>
              Use the <strong>Build Tracker</strong> tab to create and save a configuration first.
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="user-table publish-preview-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Template Heading</th>
                  <th>Item</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {expandedLevels.map((lv) => {
                  // Count total rows for this level (for rowSpan)
                  const levelRowSpan = lv.templates.reduce(
                    (acc, tpl) => acc + Math.max(tpl.items.length, 1), 0
                  );

                  // Alternate white / light blue-gray per level
                  const rowBg = lv.levelNumber % 2 === 1 ? '#ffffff' : '#f1f5f9';

                  return lv.templates.map((tpl, tplIdx) => {
                    const tplRowSpan = Math.max(tpl.items.length, 1);

                    if (tpl.items.length === 0) {
                      return (
                        <tr key={`${lv.levelNumber}-${tpl.templateId}-empty`}>
                          {tplIdx === 0 && (
                            <td rowSpan={levelRowSpan} className="publish-level-cell"
                                style={{ backgroundColor: rowBg }}>
                              <span className="verify-level-badge">Level {lv.levelNumber}</span>
                            </td>
                          )}
                          <td className="publish-heading-cell"
                              style={{ backgroundColor: rowBg }}><strong>{tpl.heading}</strong></td>
                          <td colSpan={2} style={{ backgroundColor: rowBg, color: '#94a3b8', fontStyle: 'italic' }}>
                            No items defined
                          </td>
                        </tr>
                      );
                    }

                    return tpl.items.map((itm, itmIdx) => (
                      <tr key={`${lv.levelNumber}-${tpl.templateId}-${itmIdx}`}>
                        {tplIdx === 0 && itmIdx === 0 && (
                          <td rowSpan={levelRowSpan} className="publish-level-cell"
                              style={{ backgroundColor: rowBg }}>
                            <span className="verify-level-badge">Level {lv.levelNumber}</span>
                          </td>
                        )}
                        {itmIdx === 0 && (
                          <td rowSpan={tplRowSpan} className="publish-heading-cell"
                              style={{ backgroundColor: rowBg }}>
                            <strong>{tpl.heading}</strong>
                          </td>
                        )}
                        <td style={{ backgroundColor: rowBg }}>{itm.item}</td>
                        <td style={{ backgroundColor: rowBg }}>
                          <span
                            className="badge-type-pill"
                            style={{ backgroundColor: TYPE_COLOURS[itm.type] || '#94a3b8' }}
                          >
                            {itm.type}
                          </span>
                        </td>
                      </tr>
                    ));
                  });
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default PublishTracker;